import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { instance } from '../api/instance';
import { toast } from 'react-toastify';
import { DeliveryRateResponse } from '../types';

export interface CustomDeliveryRate {
  deliveryRateId: number;
  minPrice: number;
  maxPrice: number;
  rate: number;
}

export interface TimeSurgePricing {
  startTime: string;
  endTime: string;
  multiplier: number;
  daysOfWeek: number[];
}

export interface KitchenConfigFormData {
  kitchenCode: string;
  isEnabled: boolean;
  enableSurge: boolean;
  description?: string;
  surgeMultiplier: number;
  enableWeatherPricing: boolean;
  weatherMultiplier: number;
  customDeliveryRates: CustomDeliveryRate[];
  timeSurgePricing: TimeSurgePricing[];
  id?: number; // For edit mode
}

export const useKitchenConfig = (
  onClose: () => void,
  deliveryRates: DeliveryRateResponse[] = []
) => {
  const queryClient = useQueryClient();

  const [activeKitchenType, setActiveKitchenType] = React.useState('500chow');
  const [formData, setFormData] = useState<KitchenConfigFormData>({
    kitchenCode: '',
    isEnabled: false,
    enableSurge: false,
    description: '',
    surgeMultiplier: 1.5,
    enableWeatherPricing: false,
    weatherMultiplier: 1.3,
    customDeliveryRates: [],
    timeSurgePricing: [],
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'number') {
      setFormData({
        ...formData,
        [name]: parseFloat(value),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleBooleanSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name } = e.target;
    const value = e.target.value === 'Yes';

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Custom Delivery Rates Functions
  const handleAddDeliveryRate = () => {
    setFormData({
      ...formData,
      customDeliveryRates: [
        ...formData.customDeliveryRates,
        { deliveryRateId: 0, minPrice: 0, maxPrice: 0, rate: 0 },
      ],
    });
  };

  const handleRemoveDeliveryRate = (index: number) => {
    const updatedRates = [...formData.customDeliveryRates];
    updatedRates.splice(index, 1);
    setFormData({
      ...formData,
      customDeliveryRates: updatedRates,
    });
  };

  const handleDeliveryRateChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number
  ) => {
    const { name, value, type } = e.target;
    const updatedRates = [...formData.customDeliveryRates];

    if (name === 'deliveryRateId') {
      const selectedId = parseInt(value, 10);
      const selectedRate = deliveryRates.find((dr) => dr.id === selectedId);
      if (selectedRate) {
        updatedRates[index] = {
          ...updatedRates[index],
          deliveryRateId: selectedRate.id,
          maxPrice: selectedRate.maxPrice ?? updatedRates[index].maxPrice,
          minPrice: selectedRate.minPrice ?? updatedRates[index].minPrice,
        };
      } else {
        updatedRates[index] = {
          ...updatedRates[index],
          deliveryRateId: selectedId,
        };
      }
      setFormData({
        ...formData,
        customDeliveryRates: updatedRates,
      });
      return;
    }

    if (type === 'number') {
      updatedRates[index] = {
        ...updatedRates[index],
        [name]: parseFloat(value),
      };
    } else {
      updatedRates[index] = {
        ...updatedRates[index],
        [name]: value,
      };
    }

    setFormData({
      ...formData,
      customDeliveryRates: updatedRates,
    });
  };

  // Time Surge Pricing Functions
  const handleAddTimeSurge = () => {
    setFormData({
      ...formData,
      timeSurgePricing: [
        ...formData.timeSurgePricing,
        { startTime: '', endTime: '', multiplier: 1.5, daysOfWeek: [] },
      ],
    });
  };

  const handleRemoveTimeSurge = (index: number) => {
    const updatedTimeSurge = [...formData.timeSurgePricing];
    updatedTimeSurge.splice(index, 1);
    setFormData({
      ...formData,
      timeSurgePricing: updatedTimeSurge,
    });
  };

  const handleTimeSurgeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number
  ) => {
    const { name, value, type } = e.target;
    const updatedTimeSurge = [...formData.timeSurgePricing];

    if (type === 'number') {
      updatedTimeSurge[index] = {
        ...updatedTimeSurge[index],
        [name]: parseFloat(value),
      };
    } else {
      updatedTimeSurge[index] = {
        ...updatedTimeSurge[index],
        [name]: value,
      };
    }

    setFormData({
      ...formData,
      timeSurgePricing: updatedTimeSurge,
    });
  };

  const handleDayChange = (index: number, day: number) => {
    const updatedTimeSurge = [...formData.timeSurgePricing];
    const currentDays = updatedTimeSurge[index].daysOfWeek;

    if (currentDays.includes(day)) {
      updatedTimeSurge[index].daysOfWeek = currentDays.filter((d) => d !== day);
    } else {
      updatedTimeSurge[index].daysOfWeek = [...currentDays, day];
    }

    setFormData({
      ...formData,
      timeSurgePricing: updatedTimeSurge,
    });
  };

  // Create/Update Mutation
  const createConfigMutation = useMutation(
    (data: KitchenConfigFormData) =>
      instance.post('/admin/kitchen-delivery-config', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['kitchen-delivery-config']);
        toast.success('Outlet configuration created successfully');
        onClose();
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.message || 'Error creating outlet configuration'
        );
      },
    }
  );

  const updateConfigMutation = useMutation(
    (data: KitchenConfigFormData & { id?: number }) => {
      const { ...configData } = data;
      return instance.patch(
        `/admin/kitchen-delivery-config/${configData.kitchenCode}`,
        configData
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['kitchen-delivery-config']);
        toast.success('Outlet configuration updated successfully');
        onClose();
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.message || 'Error updating outlet configuration'
        );
      },
    }
  );

  const deleteConfigMutation = useMutation(
    (code: string) => instance.delete(`/admin/kitchen-delivery-config/${code}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['kitchen-delivery-config']);
        toast.success('Outlet configuration deleted successfully');
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.message || 'Error deleting outlet configuration'
        );
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Check if we're in edit mode by looking for an ID in the form data
    if ((formData as any).id) {
      updateConfigMutation.mutate(
        formData as KitchenConfigFormData & { id: number }
      );
    } else {
      createConfigMutation.mutate(formData);
    }
  };

  const handleDelete = (code: string) => {
    if (window.confirm('Are you sure you want to delete this configuration?')) {
      deleteConfigMutation.mutate(code);
    }
  };

  return {
    formData,
    activeKitchenType,
    isSubmitting:
      createConfigMutation.isLoading || updateConfigMutation.isLoading,
    isDeleting: deleteConfigMutation.isLoading,
    setFormData,
    handleDelete,
    handleSubmit,
    handleDayChange,
    handleInputChange,
    handleAddTimeSurge,
    setActiveKitchenType,
    handleRemoveTimeSurge,
    handleAddDeliveryRate,
    handleTimeSurgeChange,
    handleRemoveDeliveryRate,
    handleBooleanSelectChange,
    handleDeliveryRateChange,
  };
};
